/**
 * orbitAssignment.ts
 * -----------------------------------------------------------------------------
 * Assigns spheres to orbits based on gravity scores.
 * Higher gravity = closer to center (inner orbits)
 *
 * Used by armillary view to prioritize what users see:
 * - Critical spheres (gravity 80-100) appear in the innermost orbit
 * - Less important spheres appear in outer orbits
 */

import { NodeData, OrbitConfig, ORBITS } from './constants';

export interface OrbitAssignment {
  orbitIndex: number;
  nodes: NodeData[];
}

/**
 * Get the orbit index for a given gravity score
 *
 * @param gravity - Gravity score (0-100)
 * @param orbits - Array of orbit configurations
 * @returns Index of the appropriate orbit, or outermost orbit if no match
 */
export function getOrbitForGravity(gravity: number, orbits: OrbitConfig[] = ORBITS): number {
  for (let i = 0; i < orbits.length; i++) {
    const orbit = orbits[i];
    if (orbit.gravityRange) {
      if (gravity >= orbit.gravityRange.min && gravity <= orbit.gravityRange.max) {
        return i;
      }
    }
  }
  // Default to outermost orbit with a gravity range
  for (let i = orbits.length - 1; i >= 0; i--) {
    if (orbits[i].gravityRange) {
      return i;
    }
  }
  return orbits.length - 1;
}

/**
 * Assigns spheres to orbits based on gravity scores
 * Higher gravity = inner orbits (closer to center)
 *
 * @param spheres - Array of NodeData with gravity scores
 * @param orbits - Array of orbit configurations (defaults to ORBITS)
 * @returns Array of arrays, where each inner array contains nodes for that orbit
 */
export function assignSpheresToOrbits(
  spheres: NodeData[],
  orbits: OrbitConfig[] = ORBITS
): NodeData[][] {
  // Initialize empty arrays for each orbit
  const orbitAssignments: NodeData[][] = orbits.map(() => []);

  // Sort spheres by gravity (highest first) for consistent ordering
  const sortedSpheres = [...spheres].sort((a, b) =>
    (b.gravity ?? 0) - (a.gravity ?? 0)
  );

  // Assign each sphere to appropriate orbit based on gravity
  for (const sphere of sortedSpheres) {
    const gravity = sphere.gravity ?? 0;
    const orbitIndex = getOrbitForGravity(gravity, orbits);
    orbitAssignments[orbitIndex].push(sphere);
  }

  return orbitAssignments;
}

/**
 * Get gravity ranges that have spheres assigned
 * Useful for debugging and visualization
 *
 * @param orbits - Orbit configurations
 * @returns Array of gravity ranges with their orbit indices
 */
export function getActiveGravityRanges(orbits: OrbitConfig[] = ORBITS): Array<{
  orbitIndex: number;
  min: number;
  max: number;
  label: string;
}> {
  const ranges: Array<{
    orbitIndex: number;
    min: number;
    max: number;
    label: string;
  }> = [];

  for (let i = 0; i < orbits.length; i++) {
    const orbit = orbits[i];
    if (orbit.gravityRange) {
      let label = 'minimal';
      if (orbit.gravityRange.min >= 80) label = 'critical';
      else if (orbit.gravityRange.min >= 60) label = 'high';
      else if (orbit.gravityRange.min >= 40) label = 'medium';
      else if (orbit.gravityRange.min >= 20) label = 'low';

      ranges.push({
        orbitIndex: i,
        min: orbit.gravityRange.min,
        max: orbit.gravityRange.max,
        label,
      });
    }
  }

  return ranges;
}
